import React from 'react';
import StorefrontHeroActions from './StorefrontHeroActions';

function EditorialTitle({ title }) {
  const lines = String(title || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) {
    return (
      <>
        <h1 className="home-hero__title home-hero__title--editorial">{title}</h1>
        <span className="home-hero__title-accent" aria-hidden="true" />
      </>
    );
  }

  const last = lines.length - 1;

  return (
    <h1 className="home-hero__title home-hero__title--editorial home-hero__title--stacked">
      {lines.map((line, i) => (
        <span
          key={`${i}-${line}`}
          className={`home-hero__title-line${i === 0 ? ' home-hero__title-line--lead' : ''}${i === last ? ' home-hero__title-line--close' : ''}`}
        >
          {line}
          {i === last ? <span className="home-hero__title-accent" aria-hidden="true" /> : null}
        </span>
      ))}
    </h1>
  );
}

/** Headline, CTAs, trust pills — shared by split, editorial, and immersive hero layouts. */
export default function HomeHeroCopyPanel({
  compact,
  brandName,
  title,
  subtitle,
  description,
  trustItems,
  trustIcon: TrustIcon,
  immersive,
  editorial,
}) {
  const luxury = immersive && !compact;
  const showcase = editorial && !compact;
  const bodyText = showcase
    ? String(description || subtitle || '').trim()
    : '';

  return (
    <div
      className={
        showcase
          ? 'home-hero__copy home-hero__copy--editorial-showcase'
          : luxury
            ? 'home-hero__copy home-hero__copy--editorial home-hero__copy--luxury'
            : immersive
              ? 'home-hero__copy home-hero__copy--editorial'
              : 'home-hero__copy home-hero__panel'
      }
    >
      <p
        className={
          showcase
            ? 'home-hero__breadcrumb'
            : compact
              ? 'home-hero__eyebrow home-hero__eyebrow--compact'
              : 'home-hero__eyebrow'
        }
      >
        {brandName}
      </p>

      {showcase ? (
        <EditorialTitle title={title} />
      ) : (
        <h1 className="home-hero__title">{title}</h1>
      )}

      {showcase && bodyText ? (
        <p className="home-hero__lead--editorial">{bodyText}</p>
      ) : null}

      {!showcase && subtitle ? (
        <p
          className={
            compact
              ? 'home-hero__subtitle home-hero__subtitle--compact'
              : 'home-hero__subtitle handwritten'
          }
        >
          {subtitle}
        </p>
      ) : null}

      {!compact && !luxury && !showcase && description ? (
        <p className="home-hero__description">{description}</p>
      ) : null}

      <StorefrontHeroActions
        placement="home"
        compact={compact}
        className={
          compact
            ? 'home-hero__actions hero-actions shop-hero-actions d-flex flex-wrap align-items-center gap-2 mt-2'
            : showcase
              ? 'home-hero__actions home-hero__actions--editorial hero-actions shop-hero-actions d-flex flex-row flex-wrap align-items-center gap-2 gap-md-3 mt-0'
              : luxury
                ? 'home-hero__actions home-hero__actions--luxury hero-actions shop-hero-actions d-flex flex-row flex-wrap align-items-center gap-2 gap-md-3 mt-0'
                : 'home-hero__actions hero-actions shop-hero-actions d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3'
        }
        giftFinderPlacement={compact || luxury || showcase ? null : 'home'}
        giftFinderClassName="btn btn-outline-secondary hero-secondary-cta btn-lg"
        primaryBtnClass={
          compact
            ? 'btn btn-primary hero-cta'
            : showcase
              ? 'home-hero__cta-main home-hero__cta-main--gradient'
              : luxury
                ? 'btn btn-primary hero-cta btn-lg home-hero__cta--luxury'
                : 'btn btn-primary hero-cta btn-lg'
        }
        secondaryBtnClass={
          compact
            ? 'btn btn-outline-secondary hero-secondary-cta btn-sm'
            : showcase
              ? 'home-hero__cta-main home-hero__cta-main--outline'
              : luxury
                ? 'btn btn-primary hero-cta btn-lg home-hero__cta--luxury'
                : 'btn btn-outline-secondary hero-secondary-cta btn-lg'
        }
      />

      {!luxury && !showcase && trustItems.length > 0 ? (
        <ul className="home-hero__trust" aria-label="Store highlights">
          {trustItems.map((item) => (
            <li key={item.key} className="home-hero__trust-item">
              <TrustIcon type={item.icon} />
              {item.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
